import { IconButton } from '@figma/fpl-components';
import { Icon24ChevronLeftLarge, Icon24ChevronRightLarge } from '@figma/fpl-icons';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { rowMarker } from './toolkit.stylex';
import type { CarouselDirection } from './useCarousel';

const styles = stylex.create({
  arrows: {
    display: 'flex',
    justifyContent: 'space-between',
    left: 0,
    opacity: {
      default: 0,
      [stylex.when.ancestor(':hover', rowMarker)]: 1,
      [stylex.when.ancestor(':focus-within', rowMarker)]: 1,
      '@media (hover: none)': 1,
    },
    pointerEvents: 'none',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    transition: {
      default: 'opacity 0.3s ease-out',
      '@media (prefers-reduced-motion: reduce)': 'none',
    },
    width: '100%',
    zIndex: 1,
  },
  inBand: {
    alignItems: 'center',
    top: 0,
    transform: 'none',
  },
  // The button can't take styles, so this holds what the row needs from it.
  arrow: {
    display: 'flex',
    pointerEvents: {
      default: 'none',
      [stylex.when.ancestor(':hover', rowMarker)]: 'auto',
      [stylex.when.ancestor(':focus-within', rowMarker)]: 'auto',
      '@media (hover: none)': 'auto',
    },
  },
  // Half of each arrow hangs past the row's edge, into the page's side padding. A phone-sized window's
  // padding is too narrow for that, so there the arrows sit inside the row instead.
  previous: {
    transform: {
      default: 'translateX(-50%)',
      '@media (max-width: 600px)': 'translateX(8px)',
    },
  },
  next: {
    transform: {
      default: 'translateX(50%)',
      '@media (max-width: 600px)': 'translateX(-8px)',
    },
  },
});

interface CarouselArrowsProps {
  rowTitle: string;
  onScroll: (direction: CarouselDirection) => void;
  /** The height of the band at the top of the row to center the arrows in. Without it, they center in the row. */
  bandStyle?: stylex.StyleXStyles;
}

/**
 * The two arrows over a carousel's left and right edges. They show while the pointer is over the
 * row or something in it has focus, and always on touch screens. The row they belong to must carry
 * `rowMarker`, which is what the hover is read from.
 */
export function CarouselArrows({ rowTitle, onScroll, bandStyle }: CarouselArrowsProps) {
  const { t } = useTranslation();

  return (
    <div {...stylex.props(styles.arrows, bandStyle ? [styles.inBand, bandStyle] : null)}>
      <span {...stylex.props(styles.arrow, styles.previous)}>
        <IconButton
          variant="primary"
          size="lg"
          aria-label={t(I18N_KEYS.TOOLKIT.PREVIOUS_IN_ROW, { row: rowTitle })}
          onClick={() => onScroll('previous')}
        >
          <Icon24ChevronLeftLarge />
        </IconButton>
      </span>
      <span {...stylex.props(styles.arrow, styles.next)}>
        <IconButton
          variant="primary"
          size="lg"
          aria-label={t(I18N_KEYS.TOOLKIT.NEXT_IN_ROW, { row: rowTitle })}
          onClick={() => onScroll('next')}
        >
          <Icon24ChevronRightLarge />
        </IconButton>
      </span>
    </div>
  );
}
